import { Controller } from '@hotwired/stimulus'

/**
 * Stimulus-контроллер для карточных слайдеров на Swiper.
 *
 * Лениво загружает Swiper и его модули, скрывает навигацию до завершения
 * инициализации, настраивает адаптивное количество карточек и уничтожает
 * созданный инстанс при отключении контроллера.
 *
 * @extends {Controller}
 */
export default class ClubPhotosController extends Controller {
  /**
   * Тексты для a11y-сообщений кнопок навигации.
   *
   * @type {{ prevText: { type: StringConstructor }, nextText: { type: StringConstructor } }}
   */
  static values = {
    prevText: {
      type: String
    },
    nextText: {
      type: String
    },
    bulletText: {
      type: String
    }
  }

  /**
   * DOM-цели контроллера.
   *
   * `container` — корневой элемент Swiper, `navigation` — обертка кнопок,
   * `prevBtn` и `nextBtn` — кнопки перехода между слайдами.
   *
   * @type {string[]}
   */
  static targets = [
    'pagination',
    'container',
    'navigation',
    'prevBtn',
    'nextBtn'
  ]

  /**
   * Активный инстанс Swiper.
   *
   * @type {import('swiper').default | null}
   */
  #swiper = null

  /**
   * Идентификатор последней попытки инициализации.
   *
   * Нужен, чтобы игнорировать результат устаревшего асинхронного импорта после
   * Turbo-перехода или повторного подключения контроллера.
   *
   * @type {number}
   */
  #initRunId = 0

  /**
   * Кеш результата динамического импорта Swiper и его модулей.
   *
   * @type {{ Swiper: import('swiper').default, Navigation: unknown, Pagination: unknown, A11y: unknown, EffectFade: unknown } | null}
   */
  static #swiperCache = null

  /**
   * Запускает асинхронную инициализацию слайдера при подключении контроллера.
   */
  connect() {
    this.#initRunId += 1
    this.#initCards(this.#initRunId)
  }

  /**
   * Отменяет ожидающую инициализацию и уничтожает текущий инстанс Swiper.
   */
  disconnect() {
    this.#initRunId += 1
    this.#swiper?.destroy(true, true)
    this.#swiper = null
  }

  /**
   * Создает Swiper для карточек и включает навигацию после готовности.
   *
   * @param {number} runId — идентификатор текущей попытки инициализации
   * @returns {Promise<void>}
   */
  async #initCards(runId) {
    this.#setNavVisible(false)
    this.#setPaginationVisible(false)

    try {
      const { Swiper, Navigation, Pagination, A11y, EffectFade } =
        await this.#importSwiper()

      if (!this.element.isConnected || runId !== this.#initRunId) return

      this.#swiper = new Swiper(this.containerTarget, {
        modules: [Navigation, Pagination, A11y, EffectFade],
        speed: 500,
        slidesPerView: 1,
        spaceBetween: 0,
        loop: true,
        loopPreventsSliding: false,
        allowTouchMove: true,
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        grabCursor: true,
        a11y: {
          prevSlideMessage: this.prevTextValue,
          nextSlideMessage: this.nextTextValue
        },
        navigation: {
          nextEl: this.hasNextBtnTarget ? this.nextBtnTarget : null,
          prevEl: this.hasPrevBtnTarget ? this.prevBtnTarget : null
        },
        pagination: this.hasPaginationTarget
          ? {
              el: this.paginationTarget,
              clickable: true,
              renderBullet: (index, className) =>
                `<button type="button" class="${className}" aria-label="${this.bulletTextValue} ${index + 1}"></button>`
            }
          : null,
        on: {
          afterInit: (swiper) => {
            this.#setNavVisible(true)
            this.#setPaginationVisible(true)

            setTimeout(() => {
              swiper.slides.forEach((slide) => {
                slide.style.transitionProperty = 'opacity'
                slide.style.transitionDuration = `${swiper.params.speed}ms`
                slide.style.transitionTimingFunction = 'ease-in-out'
              })
            }, swiper.params.speed)
          }
        }
      })
    } catch (error) {
      if (!this.element.isConnected || runId !== this.#initRunId) return
      this.#setNavVisible(false)
      this.#setPaginationVisible(false)
      console.error('Failed to initialize club photos swiper', error)
    }
  }

  /**
   * Показывает или скрывает контейнер навигации, если он есть в разметке.
   *
   * @param {boolean} visible — нужно ли показывать навигацию
   */
  #setNavVisible(visible) {
    if (this.hasNavigationTarget) {
      this.navigationTarget.style.display = visible ? '' : 'none'
    }
  }

  #setPaginationVisible(visible) {
    if (this.hasPaginationTarget) {
      this.paginationTarget.style.display = visible ? '' : 'none'
    }
  }

  /**
   * Загружает Swiper и его модули один раз на страницу.
   *
   * @returns {Promise<{ Swiper: import('swiper').default, Navigation: unknown, Pagination: unknown, A11y: unknown }>}
   */
  async #importSwiper() {
    if (!ClubPhotosController.#swiperCache) {
      const [{ default: Swiper }, modules] = await Promise.all([
        import('swiper'),
        // eslint-disable-next-line import/no-unresolved
        import('swiper/modules')
      ])
      ClubPhotosController.#swiperCache = { Swiper, ...modules }
    }
    return ClubPhotosController.#swiperCache
  }
}
